import React, { useEffect, useMemo, useState } from 'react';
import { Table, InputNumber, Input, Switch, Select, AutoComplete, Space, Empty } from 'antd';
import { appMessage as message } from '../../utils/appMessage';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, SplitCellsOutlined, DeleteOutlined } from '@ant-design/icons';

import { AppDrawer } from '../common/AppDrawer';
import { AppButton } from '../common/AppButton';
import { AppPopconfirm } from '../common/AppPopconfirm';
import { BuybackStatusTag } from './BuybackStatusTag';
import { BuybackPhotos } from './BuybackPhotos';
import { CardThumb } from './CardThumb';
import { useStockPage } from '../../contexts/StockPageContext';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { FONT_SIZE, FONT_WEIGHT, SPACING } from '../../constants/constants';
import { saveBuybackCart, markBuybackReady } from '../../api/api';
import type { BuybackDetail, BuybackItem, BuybackItemInput } from '../../types/buyback';
import { CARD_GRADE_ORDER, CARD_GRADE_LABEL } from '../../types/buyback';
import type { Card } from '../../types/card';
import { formatWon } from '../../utils/format';

// 로컬 편집용 행: 명세에 안정적인 key 를 붙인다(신규 줄은 id 가 없으므로).
type CartRow = BuybackItem & { _key: string };

let keySeq = 0;
const nextKey = () => `row-${Date.now()}-${keySeq++}`;

const toRow = (i: BuybackItem): CartRow => ({ ...i, _key: nextKey() });

const blankRow = (): CartRow => ({
  _key: nextKey(),
  id: null, cardId: null,
  productName: '', productNumber: null, sellerProductCode: null, cardType: 'OTHER',
  imageUrl: null, quantity: 1, unitPrice: null, grade: 'S', buyable: true, customerSelected: false,
});

const fromCard = (c: Card): CartRow => ({
  _key: nextKey(),
  id: null, cardId: c.id,
  productName: c.productName, productNumber: c.productNumber, sellerProductCode: c.sellerProductCode,
  cardType: c.cardType, imageUrl: c.imageUrl, quantity: 1, unitPrice: null, grade: 'S', buyable: true, customerSelected: false,
});

const won = formatWon;

// Drawer 헤더(~55) + 바디 패딩(48) + 고객정보 줄(~30) + 검색 줄(~44) + 푸터(~60) + 여백 —
// 모달(고정 340px)에서 전체 높이 Drawer로 바꾸면서 화면 높이를 실제로 다 쓰도록 계산식으로 변경.
const CART_TABLE_SCROLL_Y = 'calc(100vh - 400px)';

interface Props {
  open:    boolean;
  detail:  BuybackDetail | null;
  onClose: () => void;
  onSaved: (updated: BuybackDetail) => void; // 저장/준비완료 후 목록 갱신용
}

/**
 * 매입 카트(준비중). 고객에게 받은 카드를 담아 수량·매입가·매입 가능 여부를 설정한다.
 * 저장하면 명세가 백엔드에 전량 교체되고, "준비 완료" 를 누르면 상태가 준비 완료로 넘어간다.
 */
export const BuybackCart: React.FC<Props> = ({ open, detail, onClose, onSaved }) => {
  const t = useThemeTokens();
  const { cards } = useStockPage();

  const [rows, setRows]   = useState<CartRow[]>([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  // 모달이 열릴 때 상세 명세로 초기화
  useEffect(() => {
    if (open && detail) {
      setRows(detail.items.map(toRow));
      setSearch('');
    }
  }, [open, detail]);

  const patch = (key: string, p: Partial<CartRow>) =>
    setRows((prev) => prev.map((r) => (r._key === key ? { ...r, ...p } : r)));

  const remove = (key: string) => setRows((prev) => prev.filter((r) => r._key !== key));

  // 분할: 같은 카드를 매입 불가능 한 줄로 복제한다(수량은 직원이 조정).
  const split = (row: CartRow) =>
    setRows((prev) => {
      const idx = prev.findIndex((r) => r._key === row._key);
      const copy: CartRow = { ...row, _key: nextKey(), id: null, buyable: false, quantity: 1 };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });

  // 기존 재고 카드 검색 옵션(상품명 우선 매칭 — 판매코드는 직원이 외우기 어려워 보조 수단일
  // 뿐이라, 검색은 상품명/판매코드 둘 다 걸리되 목록은 이미지로 바로 알아볼 수 있게 큰
  // 썸네일로 보여준다, 최대 20건, 2026-07-30).
  const options = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return cards
      .filter((c) => c.productName.toLowerCase().includes(q) || c.sellerProductCode.toLowerCase().includes(q))
      .slice(0, 20)
      .map((c) => ({
        value: String(c.id),
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, padding: `${SPACING.xxs}px 0` }}>
            <CardThumb imageUrl={c.imageUrl} alt={c.productName} size={56} preview={false} />
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span style={{
                fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.medium, color: t.textPrimary,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {c.productName}
              </span>
              <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>{c.sellerProductCode}</span>
            </div>
          </div>
        ),
      }));
  }, [search, cards, t]);

  const addExisting = (value: string) => {
    const c = cards.find((x) => String(x.id) === value);
    if (c) setRows((prev) => [...prev, fromCard(c)]);
    setSearch('');
  };

  // 예상 총액 = 매입 가능 줄만 합산
  const buyableTotal = useMemo(
    () => rows.filter((r) => r.buyable).reduce((s, r) => s + (r.unitPrice ?? 0) * r.quantity, 0),
    [rows],
  );

  const validate = (): BuybackItemInput[] | null => {
    for (const r of rows) {
      if (!r.productName.trim()) { message.warning('상품명이 비어 있는 항목이 있습니다.'); return null; }
      if (r.quantity < 1) { message.warning('수량은 1 이상이어야 합니다.'); return null; }
    }
    return rows.map((r) => ({
      id: r.id ?? undefined,
      cardId: r.cardId,
      productName: r.productName.trim(),
      productNumber: r.productNumber,
      sellerProductCode: r.sellerProductCode,
      cardType: r.cardType,
      imageUrl: r.imageUrl,
      quantity: r.quantity,
      unitPrice: r.unitPrice,
      grade: r.grade ?? 'S',
      buyable: r.buyable,
    }));
  };

  const doSave = async (): Promise<BuybackDetail | null> => {
    if (!detail) return null;
    const items = validate();
    if (!items) return null;
    setSaving(true);
    try {
      const updated = await saveBuybackCart(detail.id, items);
      onSaved(updated);
      return updated;
    } catch {
      message.error('카트 저장 중 오류가 발생했습니다.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const updated = await doSave();
    if (updated) message.success('저장되었습니다.');
  };

  const handleReady = async () => {
    if (!detail) return;
    const items = validate();
    if (!items) return;
    setSaving(true);
    try {
      // 저장 → 준비 완료를 순차 실행하고, 목록 갱신(onSaved)은 마지막에 한 번만 한다.
      // (중간에 onSaved 를 부르면 저장 시점의 '준비중' 조회가 준비 완료 조회보다 늦게 도착해
      //  목록이 준비중으로 되돌아가는 경쟁 상태가 생긴다.)
      await saveBuybackCart(detail.id, items);
      const ready = await markBuybackReady(detail.id);
      onSaved(ready);
      message.success('매입 준비 완료로 변경되었습니다. 고객에게 태블릿을 보여 주세요.');
      onClose();
    } catch {
      message.error('준비 완료 처리 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<CartRow> = [
    {
      title: '상품명', dataIndex: 'productName', minWidth: 280,
      render: (v: string, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs }}>
          <CardThumb imageUrl={row.imageUrl} alt={v} />
          <Input
            value={v}
            onChange={(e) => patch(row._key, { productName: e.target.value })}
            placeholder="상품명"
            size="small"
            variant={row.cardId ? 'filled' : 'outlined'}
            readOnly={!!row.cardId}
          />
        </div>
      ),
    },
    {
      title: '판매코드', dataIndex: 'sellerProductCode', width: 150,
      render: (v: string | null, row) => (
        <Input
          value={v ?? ''}
          onChange={(e) => patch(row._key, { sellerProductCode: e.target.value || null })}
          placeholder="(수기 카드 · 선택)"
          size="small"
          readOnly={!!row.cardId}
          variant={row.cardId ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      title: '수량', dataIndex: 'quantity', width: 90, align: 'center',
      render: (v: number, row) => (
        <InputNumber min={1} value={v} onChange={(n) => patch(row._key, { quantity: Number(n) || 1 })} size="small" style={{ width: 70 }} />
      ),
    },
    {
      // 등급은 금액을 자동으로 바꾸지 않는다 — S 기준(상사 API 매입가)에서 등급이 낮으면
      // 직원이 아래 "매입 단가"를 직접 낮춰 조정한다.
      title: '등급', dataIndex: 'grade', width: 90, align: 'center',
      render: (v: CartRow['grade'], row) => (
        <Select
          value={v ?? 'S'}
          onChange={(g) => patch(row._key, { grade: g })}
          size="small" style={{ width: 70 }}
          options={CARD_GRADE_ORDER.map((g) => ({ value: g, label: CARD_GRADE_LABEL[g] }))}
        />
      ),
    },
    {
      title: '매입 단가', dataIndex: 'unitPrice', width: 130, align: 'right',
      render: (v: number | null, row) => (
        <InputNumber
          min={0} value={v ?? undefined}
          onChange={(n) => patch(row._key, { unitPrice: n == null ? null : Number(n) })}
          size="small" style={{ width: 110 }} placeholder="수기 입력"
        />
      ),
    },
    {
      title: '금액', key: 'amount', width: 110, align: 'right',
      render: (_: unknown, row) => (
        <span style={{ fontSize: FONT_SIZE.sm, color: row.buyable ? t.textPrimary : t.textMuted }}>
          {won((row.unitPrice ?? 0) * row.quantity)}
        </span>
      ),
    },
    {
      title: '매입 가능', dataIndex: 'buyable', width: 100, align: 'center',
      render: (v: boolean, row) => (
        <Switch
          checked={v}
          onChange={(c) => patch(row._key, { buyable: c })}
          checkedChildren="가능" unCheckedChildren="불가"
        />
      ),
    },
    {
      title: '', key: 'actions', width: 84, align: 'center',
      render: (_: unknown, row) => (
        <Space size={4}>
          <AppButton
            variant="text" size="sm" iconOnly icon={<SplitCellsOutlined />}
            disabledReason="매입 불가능 줄로 분할" onClick={() => split(row)}
            title="가능/불가능 분할"
          />
          <AppPopconfirm title="이 줄을 삭제하시겠습니까?" onConfirm={() => remove(row._key)}>
            <AppButton variant="text" size="sm" iconOnly icon={<DeleteOutlined />} />
          </AppPopconfirm>
        </Space>
      ),
    },
  ];

  return (
    <AppDrawer
      title={
        <Space>
          <span>매입 카트</span>
          {detail?.managementNumber && (
            <span style={{ fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.regular, color: t.textMuted }}>
              {detail.managementNumber}
            </span>
          )}
          {detail && <BuybackStatusTag status={detail.status} />}
        </Space>
      }
      open={open}
      onClose={onClose}
      placement="right"
      width={960}
      footer={detail && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: FONT_SIZE.sm, color: t.textSecondary }}>
            매입 가능 예상 총액{' '}
            <span style={{ fontSize: FONT_SIZE.lg, fontWeight: FONT_WEIGHT.bold, color: t.accent }}>{won(buyableTotal)}</span>
          </div>
          <Space>
            <AppButton onClick={onClose}>닫기</AppButton>
            {/* 준비 완료 오더는 이미 준비된 상태라, 그 자리 수정 후 저장(상태 유지)만 제공한다. */}
            <AppButton
              variant={detail.status === 'READY' ? 'primary' : 'default'}
              loading={saving}
              onClick={handleSave}
            >
              저장
            </AppButton>
            {detail.status !== 'READY' && (
              <AppPopconfirm
                title="준비 완료로 변경하시겠습니까?"
                description="저장 후 고객에게 태블릿을 보여 줄 수 있습니다."
                okText="준비 완료" danger={false}
                onConfirm={handleReady}
                disabled={rows.length === 0}
              >
                <AppButton variant="primary" disabled={rows.length === 0} disabledReason="담긴 카드가 없습니다">
                  준비 완료
                </AppButton>
              </AppPopconfirm>
            )}
          </Space>
        </div>
      )}
    >
      {detail && (
        <>
          {/* 고객 정보 */}
          <div style={{ marginBottom: SPACING.sm, fontSize: FONT_SIZE.sm, color: t.textSecondary }}>
            고객 <span style={{ color: t.textPrimary, fontWeight: FONT_WEIGHT.semibold }}>{detail.name ?? '-'}</span>
            <span style={{ margin: `0 ${SPACING.xs}px`, color: t.border }}>|</span>
            {detail.phone ?? '-'}
          </div>

          {/* 실물 카드 사진(선택 첨부) */}
          <BuybackPhotos buybackId={detail.id} />

          {/* 카드 추가 */}
          <div style={{ display: 'flex', gap: SPACING.xs, marginBottom: SPACING.sm }}>
            <AutoComplete
              value={search}
              options={options}
              onChange={setSearch}
              onSelect={addExisting}
              style={{ flex: 1 }}
              placeholder="재고 카드 검색(상품명·판매코드)"
              allowClear
              virtual={false}
              listHeight={420}
            />
            <AppButton icon={<PlusOutlined />} onClick={() => setRows((prev) => [...prev, blankRow()])}>
              수기 카드 추가
            </AppButton>
          </div>

          <Table
            rowKey="_key"
            columns={columns}
            dataSource={rows}
            pagination={false}
            size="small"
            bordered
            scroll={{ y: CART_TABLE_SCROLL_Y }}
            locale={{
              emptyText: <Empty description="담긴 카드가 없습니다" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
            }}
          />
        </>
      )}
    </AppDrawer>
  );
};
